import { BaseSopOperation } from "./_Base";
import { CoreGroup } from "../../../core/geometry/Group";
import { Vector3 } from "three";
import { TypeAssert } from "../../../engine/poly/Assert";
import {
  CoreTransform,
  ROTATION_ORDERS,
  RotationOrder,
  TransformTargetType,
  TRANSFORM_TARGET_TYPES
} from "../../../core/Transform";
import { InputCloneMode } from "../../../engine/poly/InputCloneMode";
import { MathUtils } from "three";
export var TransformObjectMode = /* @__PURE__ */ ((TransformObjectMode2) => {
  TransformObjectMode2["SET_PARAMS"] = "set params";
  TransformObjectMode2["UPDATE_MATRIX"] = "update matrix";
  return TransformObjectMode2;
})(TransformObjectMode || {});
export const TRANSFORM_OBJECT_MODES = [
  "set params" /* SET_PARAMS */,
  "update matrix" /* UPDATE_MATRIX */
];
export class TransformSopOperation extends BaseSopOperation {
  constructor() {
    super(...arguments);
    this._coreTransform = new CoreTransform();
    this._point_pos = new Vector3();
    this._object_scale = new Vector3();
    this._r = new Vector3();
    this._object_position = new Vector3();
  }
  static type() {
    return "transform";
  }
  cook(inputCoreGroups, params) {
    const objects = inputCoreGroups[0].objects();
    this._applyTransform(objects, params);
    return inputCoreGroups[0];
  }
  _applyTransform(objects, params) {
    const mode = TRANSFORM_TARGET_TYPES[params.applyOn];
    switch (mode) {
      case TransformTargetType.GEOMETRIES: {
        return this._updateGeometries(objects, params);
      }
      case TransformTargetType.OBJECTS: {
        return this._updateObjects(objects, params);
      }
    }
    TypeAssert.unreachable(mode);
  }
  _updateGeometries(objects, params) {
    const matrix = this._matrix(params);
    if (params.group.trim() === "") {
      for (let object of objects) {
        const geometry = object.geometry;
        if (geometry) {
          geometry.translate(-params.pivot.x, -params.pivot.y, -params.pivot.z);
          geometry.applyMatrix4(matrix);
          geometry.translate(params.pivot.x, params.pivot.y, params.pivot.z);
        }
      }
    } else {
      const core_group = CoreGroup._fromObjects(objects);
      const points = core_group.pointsFromGroup(params.group);
      for (let point of points) {
        const position = point.getPosition(this._point_pos).sub(params.pivot);
        position.applyMatrix4(matrix);
        point.setPosition(position.add(params.pivot));
      }
    }
  }
  _updateObjects(objects, params) {
    const objectMode = TRANSFORM_OBJECT_MODES[params.objectMode];
    switch (objectMode) {
      case "set params" /* SET_PARAMS */: {
        return this._update_objects_params(objects, params);
      }
      case "update matrix" /* UPDATE_MATRIX */: {
        return this._update_objects_matrix(objects, params);
      }
    }
    TypeAssert.unreachable(objectMode);
  }
  _update_objects_params(objects, params) {
    for (let object of objects) {
      object.position.copy(params.t);
      const order = ROTATION_ORDERS[params.rotationOrder];
      this._r.copy(params.r).multiplyScalar(MathUtils.DEG2RAD);
      object.rotation.set(this._r.x, this._r.y, this._r.z, order);
      this._object_scale.copy(params.s).multiplyScalar(params.scale);
      object.scale.copy(this._object_scale);
      object.updateMatrix();
    }
  }
  _update_objects_matrix(objects, params) {
    const matrix = this._matrix(params);
    for (let object of objects) {
      this._object_position.copy(object.position);
      object.position.multiplyScalar(0);
      object.updateMatrix();
      object.applyMatrix4(matrix);
      object.position.add(this._object_position);
      object.updateMatrix();
    }
  }
  _matrix(params) {
    return this._coreTransform.matrix(params.t, params.r, params.s, params.scale, ROTATION_ORDERS[params.rotationOrder]);
  }
}
TransformSopOperation.DEFAULT_PARAMS = {
  applyOn: TRANSFORM_TARGET_TYPES.indexOf(TransformTargetType.GEOMETRIES),
  objectMode: TRANSFORM_OBJECT_MODES.indexOf("set params" /* SET_PARAMS */),
  group: "",
  rotationOrder: ROTATION_ORDERS.indexOf(RotationOrder.XYZ),
  t: new Vector3(0, 0, 0),
  r: new Vector3(0, 0, 0),
  s: new Vector3(1, 1, 1),
  scale: 1,
  pivot: new Vector3(0, 0, 0)
};
TransformSopOperation.INPUT_CLONED_STATE = InputCloneMode.FROM_NODE;
